<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { DoubleMaterialityIroImpactType } from '@/__generated__/types';
import { calculateMaterialityForPotentialNegative } from '../../../utils';
import { useStore } from '../../../store';
import OgScore, { type RowItem } from '../OgScore.vue';

const { t } = useI18n();
const store = useStore();

const headers = computed(() => [
  { text: t('Impacts'), value: 'impact' },
  { text: t('Scale'), value: 'scale', columnClasses: 'w-[150px]' },
  { text: t('Scope'), value: 'scope', columnClasses: 'w-[150px]' },
  {
    text: t('Irremediability'),
    value: 'irremediability',
    columnClasses: 'w-[150px]',
  },
  { text: t('Likelihood'), value: 'likelihood', columnClasses: 'w-[150px]' },
  { text: t('Materiality'), value: 'materiality', columnClasses: 'w-[100px]' },
]);

const items = computed<RowItem[]>(() =>
  store.value.iroImpacts
    .filter(
      (iroImpact) =>
        iroImpact.type === DoubleMaterialityIroImpactType.POTENTIAL_NEGATIVE,
    )
    .map((iroImpact) => ({
      _id: iroImpact._id,
      impact: iroImpact,
      riskAndOpportunity: null,
      topic: store.value.topics.find(
        (topic) => topic._id === iroImpact.topicId,
      )!,
      materiality: calculateMaterialityForPotentialNegative(
        iroImpact.scale,
        iroImpact.scope,
        iroImpact.irremediability,
        iroImpact.likelihood,
      ),
    })),
);
</script>

<template>
  <OgScore
    :headers="headers"
    :items="items"
    :title="t('Identified negative potential impacts:')"
    infoType="error"
    goBackRoute="doubleMaterialityIROsScoreActualNegative"
    nextRoute="doubleMaterialityIROsScoreActualPositive"
  >
    <p class="mb-4">
      {{
        t(
          'For negative potential impacts, materiality is determined by the severity of the impact, which consists of 3 criteria, as well as the likelihood of the impact. Please, rate these 4 criteria from 1 (being the lowest) to 5 (being the highest).',
        )
      }}
    </p>
    <ul class="list-disc pl-6">
      <li>
        {{
          t(
            'The scale: how grave or beneficial the impact is, e.g. does the impact lead to non-compliance with laws or regulations?',
          )
        }}
      </li>
      <li>
        {{
          t(
            'The scope: how widespread the impact is, e.g. the number of individuals or countries affected.',
          )
        }}
      </li>
      <li>
        {{
          t(
            'Irremediability: any limits on the ability to restore those affected to a situation at least the same as, or equivalent to, their situation before the impact.',
          )
        }}
      </li>
      <li>
        {{
          t(
            'Likelihood: the likelihood that the potential impact actually occurs.',
          )
        }}
      </li>
    </ul>
  </OgScore>
</template>
